import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FilePenLine,
  Gavel,
  Loader2,
  Play,
} from 'lucide-react';
import { PORTAL_BTN } from '../../arkorbit-ui/portalTheme';
import { isLaborPortalHostname } from '../../../constants';
import {
  isJudicatureDemoJobHost,
  isJudicatureDemoWorkerUnavailable,
} from '../judicatureDemoGates';
import {
  fetchJudicatureDemoArtifact,
  getJudicatureDemoJob,
  JudicatureJobApiError,
  startJudicatureDemoJob,
  uploadJudicatureDemoDocxToDocuments,
  type JudicatureArtifactFormat,
  type JudicatureLitigationJob,
} from '../judicatureLitigationJobs';
import type { PortalDocumentOpenFields } from '../portalDocuments';

export interface JudicatureDemoJobCardProps {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  onDocumentReady: (fields: PortalDocumentOpenFields) => void | Promise<void>;
}

const STATUS_LABELS: Record<JudicatureLitigationJob['status'], string> = {
  queued: 'Wartet',
  running: 'Analyse läuft',
  succeeded: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
};

function errorLabel(error: unknown): string {
  if (error instanceof JudicatureJobApiError) return error.code;
  return error instanceof Error ? error.message : 'UNBEKANNTER_FEHLER';
}

export const JudicatureDemoJobCard: React.FC<JudicatureDemoJobCardProps> = ({
  customerId,
  customerStatus,
  customerEmail,
  onDocumentReady,
}) => {
  const [job, setJob] = React.useState<JudicatureLitigationJob | null>(null);
  const [error, setError] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [artifactBusy, setArtifactBusy] = React.useState<string | null>(null);
  const [workerDown, setWorkerDown] = React.useState(false);

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLabor = Boolean(hostname) && isLaborPortalHostname(hostname);
  const demoHostOk = isJudicatureDemoJobHost(hostname);
  const startBlocked = workerDown || !demoHostOk;

  React.useEffect(() => {
    if (workerDown) return undefined;
    if (!job || job.status === 'succeeded' || job.status === 'failed') return undefined;
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const next = await getJudicatureDemoJob(job.job_id);
        if (cancelled) return;
        setJob(next);
        setError('');
        if (next.status !== 'succeeded' && next.status !== 'failed') {
          timer = window.setTimeout(poll, 3_000);
        }
      } catch (err) {
        if (cancelled) return;
        const code = errorLabel(err);
        setError(code);
        if (isJudicatureDemoWorkerUnavailable(code)) {
          setWorkerDown(true);
          return;
        }
        timer = window.setTimeout(poll, 5_000);
      }
    };

    timer = window.setTimeout(poll, 2_000);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [job?.job_id, workerDown]);

  if (!isLabor) return null;

  const startDemo = async () => {
    if (startBlocked) return;
    setStarting(true);
    setError('');
    try {
      setJob(await startJudicatureDemoJob(customerId));
    } catch (err) {
      const code = errorLabel(err);
      setError(code);
      if (isJudicatureDemoWorkerUnavailable(code)) {
        setWorkerDown(true);
      }
    } finally {
      setStarting(false);
    }
  };

  const downloadArtifact = async (format: JudicatureArtifactFormat) => {
    if (!job) return;
    setArtifactBusy(`download-${format}`);
    setError('');
    try {
      const artifact = await fetchJudicatureDemoArtifact(job.job_id, format);
      const href = URL.createObjectURL(artifact.blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = artifact.filename;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(href), 1_000);
    } catch (err) {
      const code = errorLabel(err);
      setError(code);
      if (isJudicatureDemoWorkerUnavailable(code)) {
        setWorkerDown(true);
      }
    } finally {
      setArtifactBusy(null);
    }
  };

  const openDocx = async () => {
    if (!job) return;
    setArtifactBusy('open-docx');
    setError('');
    try {
      const fields = await uploadJudicatureDemoDocxToDocuments({
        jobId: job.job_id,
        customerId,
        customerStatus,
        customerEmail,
      });
      await onDocumentReady(fields);
    } catch (err) {
      const code = errorLabel(err);
      setError(code);
      if (isJudicatureDemoWorkerUnavailable(code)) {
        setWorkerDown(true);
      }
    } finally {
      setArtifactBusy(null);
    }
  };

  const artifactFormats = new Set(job?.artifacts?.map((item) => item.format) ?? []);
  const terminal = job?.status === 'succeeded' || job?.status === 'failed';

  return (
    <section
      className="space-y-4 rounded-lg border border-violet-400/30 bg-[#07070a] p-5"
      data-testid="judicature-demo-job-card"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg border border-violet-500/25 bg-violet-500/10 p-2.5">
          <Gavel className="h-5 w-5 text-violet-300" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-white">Fallanalyse</h2>
            <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-200">
              Übungsfall
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-400">
            Quellengebundene Fallanalyse auf Norm und OGH-Judikatur. Das ist keine
            Normen-Suche.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-violet-400/20 bg-[#07070a] p-3 text-xs text-slate-300">
        <p className="font-semibold text-slate-200">Feste Übungs-Fakten</p>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>Schriftliches Enddatum in einem fiktiven Mietvertrag</li>
          <li>Keine dokumentierte Anerkennung eines unbefristeten Mietverhältnisses</li>
        </ul>
        <p className="mt-2 text-slate-500">Keine echten Akten und keine personenbezogenen Daten eingeben.</p>
      </div>

      {!demoHostOk && (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2"
          data-testid="judicature-demo-host-blocked"
        >
          <p className="text-xs font-semibold text-amber-200">
            Übungsfall nur in der Übungs-Umgebung — nicht auf der produktiven Fläche.
          </p>
        </div>
      )}

      {workerDown && (
        <div
          className="rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-2"
          data-testid="judicature-demo-worker-down"
        >
          <p className="text-xs font-semibold text-slate-200">
            Fallanalyse gerade nicht erreichbar. Keine laufende Analyse.
          </p>
        </div>
      )}

      {job && !workerDown && (
        <div className="rounded-lg border border-violet-400/20 bg-[#07070a] p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {job.status === 'succeeded' ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden />
              ) : job.status === 'failed' ? (
                <AlertTriangle className="h-4 w-4 text-red-400" aria-hidden />
              ) : (
                <Loader2 className="h-4 w-4 animate-spin text-violet-300" aria-hidden />
              )}
              <span className="text-sm font-bold text-white">{STATUS_LABELS[job.status]}</span>
            </div>
          </div>

          {job.status === 'failed' && (
            <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2">
              <p className="text-xs font-bold text-red-300">
                Analyse nicht abgeschlossen.
              </p>
              {job.gate_violations?.length ? (
                <p className="mt-1 text-[10px] text-red-200/80">
                  Der Übungsfall erfüllt die Qualitätsprüfung nicht — es wird kein Ergebnis ausgegeben.
                </p>
              ) : null}
            </div>
          )}
        </div>
      )}

      {error && !workerDown && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2">
          <p className="text-xs font-bold text-red-300">Die Fallanalyse ist fehlgeschlagen. Bitte später erneut versuchen.</p>
        </div>
      )}

      {job?.status === 'succeeded' && !workerDown ? (
        <div className="grid gap-2 sm:grid-cols-3">
          {(['docx', 'pdf'] as const).map((format) => (
            <button
              key={format}
              type="button"
              disabled={!artifactFormats.has(format) || artifactBusy !== null}
              onClick={() => void downloadArtifact(format)}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium disabled:cursor-not-allowed ${
                !artifactFormats.has(format) || artifactBusy !== null
                  ? PORTAL_BTN.secondaryDisabled
                  : PORTAL_BTN.secondary
              }`}
            >
              {artifactBusy === `download-${format}` ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              {format.toUpperCase()}
            </button>
          ))}
          <button
            type="button"
            disabled={!artifactFormats.has('docx') || artifactBusy !== null}
            onClick={() => void openDocx()}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium disabled:cursor-not-allowed ${
              !artifactFormats.has('docx') || artifactBusy !== null
                ? PORTAL_BTN.primaryDisabled
                : PORTAL_BTN.primary
            }`}
          >
            {artifactBusy === 'open-docx' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FilePenLine className="h-4 w-4" aria-hidden />
            )}
            Weiterbearbeiten
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={starting || startBlocked || (!!job && !terminal && !workerDown)}
          onClick={() => void startDemo()}
          data-testid="judicature-demo-start"
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed ${
            starting || startBlocked || (!!job && !terminal && !workerDown)
              ? PORTAL_BTN.primaryDisabled
              : PORTAL_BTN.primary
          }`}
        >
          {starting || (!!job && !terminal && !workerDown && !startBlocked) ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Play className="h-4 w-4" aria-hidden />
          )}
          {workerDown
            ? 'Gerade nicht erreichbar'
            : !demoHostOk
              ? 'Nur in der Übungs-Umgebung'
              : job?.status === 'failed'
                ? 'Erneut starten'
                : 'Übungsfall starten'}
        </button>
      )}

      <p className="text-[10px] leading-relaxed text-slate-500">
        Jeder Export ist ein Entwurf. Die anwaltliche Prüfung bleibt zwingend;
        das System leistet keine Rechtsberatung.
      </p>
    </section>
  );
};
